import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { Button, Card, Cluster, EmptyState, Input, Stack } from "@salesapp/ui";
import { mailboxApi, type MailboxSettings, type MailMessage } from "../../lib/mailboxApi";
import { useLocale } from "../../shell/LocaleProvider";
import { useCrmHref } from "../../lib/customerUrl";
import { ListWorkspace } from "./RecordWorkspace";
import { CompanyLookup } from "./CompanyLookup";

const VIEWS = [
  { key: "open", label: "Open" },
  { key: "new", label: "New" },
  { key: "claimed", label: "Claimed" },
  { key: "snoozed", label: "Snoozed" },
  { key: "converted", label: "Converted" },
  { key: "ignored", label: "Ignored" },
] as const;

const INTENTS = [
  { key: "", label: "Any" },
  { key: "lead", label: "Lead" },
  { key: "support", label: "Support" },
  { key: "follow_up", label: "Follow up" },
  { key: "meeting", label: "Meeting" },
  { key: "other", label: "Not sales" },
  { key: "unclassified", label: "Not classified" },
] as const;

const INTENT_LABEL: Record<string, string> = {
  lead: "Lead",
  support: "Support",
  follow_up: "Follow up",
  meeting: "Meeting",
  other: "Not sales",
  unclassified: "Not classified",
};

function intentClass(intent: string): string {
  if (intent === "lead") return "crm-badge crm-badge--pending";
  if (intent === "support") return "crm-badge crm-badge--aging";
  if (intent === "follow_up") return "crm-badge crm-badge--issued";
  if (intent === "meeting") return "crm-badge crm-badge--active";
  return "crm-badge crm-badge--draft";
}

function waitingLabel(minutes: number): string {
  if (!minutes) return "";
  if (minutes < 60) return `${minutes}m waiting`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h waiting`;
  return `${Math.floor(hours / 24)}d waiting`;
}

function localInput(when: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}T${pad(when.getHours())}:${pad(when.getMinutes())}`;
}

function mailboxStatus(
  view: string,
  intent: string,
  count: number,
  ready: boolean,
  lastScan: string | null,
  format: (value: string) => string,
): string {
  const when = lastScan ? `Last read ${format(lastScan)}.` : "The mailbox has not been read yet.";
  if (!ready || count > 0) return when;
  if (intent) return `No email matches this filter. ${when}`;
  if (view === "open" || view === "new") return `All emails processed. No new email. ${when}`;
  return `Nothing in this view. ${when}`;
}

export function MailboxPage({ orgId }: { orgId: string }) {
  const { formatDateTime } = useLocale();
  const href = useCrmHref();
  const [searchParams, setSearchParams] = useSearchParams();
  const [settings, setSettings] = useState<MailboxSettings | null>(null);
  const [view, setView] = useState<(typeof VIEWS)[number]["key"]>("open");
  const [intent, setIntent] = useState("");
  const [messages, setMessages] = useState<MailMessage[]>([]);
  const [selected, setSelected] = useState<MailMessage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [companyId, setCompanyId] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [starts, setStarts] = useState("");
  const [note, setNote] = useState("");
  const [composing, setComposing] = useState(false);
  const [draftFrom, setDraftFrom] = useState("");
  const [draftName, setDraftName] = useState("");
  const [draftSubject, setDraftSubject] = useState("");
  const [draftBody, setDraftBody] = useState("");
  const [listReady, setListReady] = useState(false);

  const loadList = useCallback(async () => {
    const row = await mailboxApi.messages(orgId, view, intent || undefined);
    setMessages(row.messages);
    return row.messages;
  }, [orgId, view, intent]);

  const openMessage = useCallback(async (id: string) => {
    const row = await mailboxApi.message(orgId, id);
    setSelected(row.message);
    setNote(row.message.internal_note || "");
    setCompanyId(row.message.company_id || "");
    setSearchParams((current) => {
      if (current.get("message") === id) return current;
      const next = new URLSearchParams(current);
      next.set("message", id);
      return next;
    }, { replace: true });
  }, [orgId, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    mailboxApi.settings(orgId).then((row) => {
      if (!cancelled) setSettings(row.mailbox);
    }).catch((err) => {
      if (!cancelled) setError(err instanceof Error ? err.message : "Mailbox could not be loaded.");
    });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const requestedMessage = searchParams.get("message");
  useEffect(() => {
    if (!settings?.enabled) return;
    let cancelled = false;
    setListReady(false);
    loadList().then((rows) => {
      if (cancelled) return;
      const id = requestedMessage && rows.some((item) => item.id === requestedMessage)
        ? requestedMessage
        : rows[0]?.id;
      if (id) {
        const start = new Date();
        start.setHours(start.getHours() + 1, 0, 0, 0);
        setStarts(localInput(start));
        void openMessage(id).catch(() => undefined);
      } else setSelected(null);
    }).catch((err) => {
      if (!cancelled) setError(err instanceof Error ? err.message : "Messages could not be loaded.");
    }).finally(() => {
      if (!cancelled) setListReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [settings?.enabled, loadList, requestedMessage, openMessage]);

  async function refresh() {
    setBusy(true);
    setError(null);
    try {
      const result = await mailboxApi.scan(orgId);
      setNotice(`Checked the mailbox. ${result.stored} new.`);
      const [settingsRow] = await Promise.all([mailboxApi.settings(orgId), loadList()]);
      setSettings(settingsRow.mailbox);
      setListReady(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "The mailbox could not be checked.");
    } finally {
      setBusy(false);
    }
  }

  async function act(work: () => Promise<{ message: MailMessage }>, done: string) {
    setBusy(true);
    setError(null);
    try {
      const row = await work();
      setSelected(row.message);
      setNotice(done);
      await loadList();
    } catch (err) {
      setError(err instanceof Error ? err.message : "That action could not be completed.");
    } finally {
      setBusy(false);
    }
  }

  if (!settings) {
    return <ListWorkspace title="Mailbox">{error ? <p className="crm-error">{error}</p> : <p>Loading mailbox…</p>}</ListWorkspace>;
  }

  if (!settings.enabled) {
    return (
      <ListWorkspace title="Mailbox">
        <Card>
          <EmptyState
            title="The shared mailbox is off"
            description="An organisation admin turns it on under Organisation settings → App Settings, then connects the company mailbox."
          />
        </Card>
      </ListWorkspace>
    );
  }

  return (
    <ListWorkspace
      title="Mailbox"
      actions={(
        <Cluster gap={3} align="center">
          <p className="crm-muted mailbox-status" data-testid="mailbox-last-read">
            {mailboxStatus(view, intent, messages.length, listReady, settings.last_scan_at, formatDateTime)}
          </p>
          <Button variant="secondary" onClick={() => void refresh()} disabled={busy}>Check for new mail</Button>
        </Cluster>
      )}
    >
      {error ? <p role="alert" className="crm-error">{error}</p> : null}
      {notice ? <p className="home-subtitle">{notice}</p> : null}
      <Stack gap={3} className="mailbox-filters">
        <Cluster gap={3}>
          {VIEWS.map((item) => (
            <Button
              key={item.key}
              size="sm"
              type="button"
              variant={view === item.key ? "primary" : "secondary"}
              onClick={() => {
                setView(item.key);
                setComposing(item.key === "new");
              }}
            >
              {item.label}
            </Button>
          ))}
        </Cluster>
        <Cluster gap={3}>
          {INTENTS.map((item) => (
            <Button key={item.key || "any"} size="sm" variant={intent === item.key ? "primary" : "ghost"} onClick={() => setIntent(item.key)}>
              {item.label}
            </Button>
          ))}
        </Cluster>
      </Stack>
      {composing ? (
        <Card>
          <Stack gap={3}>
            <h2 className="home-section-title">Log a message</h2>
            <p className="crm-muted">Add a message that did not arrive by mail. It shows up under New so you can turn it into a lead, a meeting, or a follow-up.</p>
            <Cluster gap={3}>
              <Input label="From name" value={draftName} onChange={(event) => setDraftName(event.target.value)} />
              <Input label="From email" type="email" value={draftFrom} onChange={(event) => setDraftFrom(event.target.value)} />
            </Cluster>
            <Input label="Subject" value={draftSubject} onChange={(event) => setDraftSubject(event.target.value)} />
            <Input label="Message" value={draftBody} onChange={(event) => setDraftBody(event.target.value)} />
            <Cluster gap={2}>
              <Button
                type="button"
                disabled={busy || !draftSubject.trim()}
                onClick={() => void act(async () => {
                  const row = await mailboxApi.log(orgId, {
                    from_name: draftName,
                    from_email: draftFrom,
                    subject: draftSubject,
                    body_text: draftBody,
                  });
                  setDraftFrom("");
                  setDraftName("");
                  setDraftSubject("");
                  setDraftBody("");
                  setComposing(false);
                  return row;
                }, "Message logged.")}
              >
                Save message
              </Button>
              <Button type="button" variant="secondary" onClick={() => setComposing(false)}>Cancel</Button>
            </Cluster>
          </Stack>
        </Card>
      ) : null}
      <div className="mailbox-layout">
        <div className="mailbox-list" data-testid="mailbox-list">
          {listReady && messages.length === 0 ? (
            <p className="crm-muted mailbox-empty" data-testid="mailbox-empty">
              {mailboxStatus(view, intent, 0, true, settings.last_scan_at, formatDateTime)}
            </p>
          ) : null}
          {messages.map((item) => (
            <button
              key={item.id}
              type="button"
              className={selected?.id === item.id ? "mailbox-row is-selected" : "mailbox-row"}
              onClick={() => void openMessage(item.id)}
            >
              <Cluster gap={2}>
                <strong>{item.from_name || item.from_email || "Unknown sender"}</strong>
                <span className={intentClass(item.intent)}>{INTENT_LABEL[item.intent] || item.intent}</span>
              </Cluster>
              <span>{item.subject || "(no subject)"}</span>
              <span className="crm-muted">
                {formatDateTime(item.received_at)}
                {waitingLabel(item.waiting_minutes) ? ` · ${waitingLabel(item.waiting_minutes)}` : ""}
                {item.thread_size > 1 ? ` · ${item.thread_size} in this thread` : ""}
                {item.claimed_name ? ` · ${item.claimed_name} is on this` : ""}
              </span>
            </button>
          ))}
        </div>
        {selected ? (
          <Card>
            <Stack gap={3} data-testid="mailbox-detail">
              <h2 className="home-section-title">{selected.subject || "(no subject)"}</h2>
              <p className="crm-muted">
                {selected.from_name} {selected.from_email ? `<${selected.from_email}>` : ""}
              </p>
              {selected.summary ? <p>{selected.summary}</p> : null}
              {selected.ai_error ? (
                <p className="crm-muted">AI could not classify this message. You can still create a record from it yourself.</p>
              ) : null}
              <div className="mailbox-create" data-testid="mailbox-create">
                <Stack gap={3}>
                  <h3 className="home-section-title">Create from this email</h3>
                  <p className="crm-muted">Turn this message into a lead, a support case, a meeting, or a follow-up. Link a company when you have one.</p>
                  <CompanyLookup
                    orgId={orgId}
                    value={companyName}
                    companyId={companyId}
                    onChange={({ name, companyId: nextId }) => {
                      setCompanyName(name);
                      setCompanyId(nextId);
                    }}
                  />
                  <Input label="Meeting start" type="datetime-local" value={starts} onChange={(event) => setStarts(event.target.value)} />
                  <Cluster gap={2}>
                    <Button size="sm" disabled={busy} onClick={() => void act(() => mailboxApi.claim(orgId, selected.id), "You are on this message.")}>
                      Claim
                    </Button>
                    <Button size="sm" variant="secondary" disabled={busy} onClick={() => void act(
                      () => mailboxApi.convert(orgId, selected.id, { kind: "lead", company_id: companyId || undefined }),
                      "Converted to a lead.",
                    )}>
                      Make a lead
                    </Button>
                    <Button size="sm" variant="secondary" disabled={busy} onClick={() => void act(
                      () => mailboxApi.convert(orgId, selected.id, { kind: "support", company_id: companyId || undefined }),
                      "Converted to a support case.",
                    )}>
                      Support case
                    </Button>
                    <Button size="sm" variant="secondary" disabled={busy} onClick={() => void act(
                      () => mailboxApi.convert(orgId, selected.id, {
                        kind: "meeting",
                        company_id: companyId || undefined,
                        starts_at: starts ? new Date(starts).toISOString() : undefined,
                      }),
                      "Meeting added to your calendar.",
                    )}>
                      Meeting
                    </Button>
                    <Button size="sm" variant="secondary" disabled={busy} onClick={() => void act(
                      () => mailboxApi.convert(orgId, selected.id, { kind: "follow_up", company_id: companyId || undefined }),
                      "Follow-up added to your calendar.",
                    )}>
                      Follow up
                    </Button>
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => {
                      const until = new Date();
                      until.setDate(until.getDate() + 1);
                      until.setHours(9, 0, 0, 0);
                      void act(() => mailboxApi.snooze(orgId, selected.id, until.toISOString()), "Snoozed until tomorrow morning.");
                    }}>
                      Snooze
                    </Button>
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => void act(() => mailboxApi.ignore(orgId, selected.id), "Ignored.")}>
                      Ignore
                    </Button>
                  </Cluster>
                  {selected.lead_id ? <a href={href(`/leads/${selected.lead_id}`)}>Open the lead</a> : null}
                  {selected.ticket_id ? <a href={href(`/support/${selected.ticket_id}`)}>Open the support case</a> : null}
                </Stack>
              </div>
              <pre className="mailbox-body">{selected.body_text || "This message has no text."}</pre>
              {selected.suggested_reply ? (
                <Stack gap={2}>
                  <p className="home-section-title">Suggested reply</p>
                  <p>{selected.suggested_reply}</p>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => void navigator.clipboard?.writeText(selected.suggested_reply)}
                  >
                    Copy reply
                  </Button>
                </Stack>
              ) : null}
              {(selected.thread?.length || 0) > 1 ? (
                <Stack gap={2}>
                  <p className="home-section-title">Thread</p>
                  {selected.thread?.map((item) => (
                    <button key={item.id} type="button" className="mailbox-text-button" onClick={() => void openMessage(item.id)}>
                      {formatDateTime(item.received_at)} — {item.subject}
                    </button>
                  ))}
                </Stack>
              ) : null}
              <Input label="Internal note" value={note} onChange={(event) => setNote(event.target.value)} />
              <Button
                size="sm"
                variant="secondary"
                disabled={busy}
                onClick={() => void act(() => mailboxApi.note(orgId, selected.id, note), "Note saved.")}
              >
                Save note
              </Button>
            </Stack>
          </Card>
        ) : (
          <Card>
            <p className="crm-muted">
              {listReady && messages.length === 0
                ? mailboxStatus(view, intent, 0, true, settings.last_scan_at, formatDateTime)
                : "Select a message."}
            </p>
          </Card>
        )}
      </div>
    </ListWorkspace>
  );
}
